/**
 * SecureEmbed Energy Portal - Tooltip Content Registry
 *
 * Central registry of explanation entries for all analytics elements.
 * Each entry describes what a figure is, how it was derived, and its significance.
 */

export interface TooltipContent {
  what: string;
  how: string;
  significance: string;
}

export const TOOLTIPS: Record<string, TooltipContent> = {
  // ─── Analytics Page: Stat Cards ────────────────────────────────────────
  'analytics.stat.total-insights': {
    what: 'Total number of data records processed by the ingestion pipeline.',
    how: 'Sum of recordsProcessed across all SUCCEEDED pipeline runs from GET /api/ingestion?env=dev, filtered to the selected time range (1d/7d/30d/90d).',
    significance: 'Measures overall pipeline throughput. Growth indicates healthy data ingestion; a sudden drop may signal pipeline failures.',
  },
  'analytics.stat.insight-types': {
    what: 'Count of distinct insight categories generated by the analytics engine.',
    how: 'Number of unique keys in the by_type object returned by GET /api/analytics. Types include anomaly_detection, pattern_recognition, cluster_analysis, etc.',
    significance: 'A healthy system produces multiple insight types. If only one type appears, the analytics engine may be misconfigured or data variety is low.',
  },
  'analytics.stat.embeddings-used': {
    what: 'Number of unique source embeddings referenced by generated insights.',
    how: 'The unique_source_embeddings field from the analytics summary endpoint, representing distinct embedding vectors that contributed to insights.',
    significance: 'Higher values indicate broader data coverage. Low values relative to total insights suggest the system is over-analysing a narrow data slice.',
  },
  'analytics.stat.avg-confidence': {
    what: 'Mean confidence score across all insights in the selected period.',
    how: 'From performance.confidence.avg in the analytics response. Each insight\'s ML model outputs a confidence between 0 and 1, and this is the arithmetic mean.',
    significance: 'Scores above 85% indicate reliable model output. A declining average may signal data drift or model degradation requiring retraining.',
  },

  // ─── Analytics Page: Datasource Cards ──────────────────────────────────
  'analytics.ds.aemo': {
    what: 'Total indexed records from the Australian Energy Market Operator (AEMO) data feed.',
    how: 'Count from GET /api/metadata/stats record_counts.aemo. Includes NEM spot prices, demand, generation, and interconnector data.',
    significance: 'AEMO is the primary datasource. Record counts should grow with each hourly pipeline run. Stale counts indicate ingestion failures.',
  },
  'analytics.ds.bom': {
    what: 'Total indexed records from the Bureau of Meteorology (BOM) weather data.',
    how: 'Count from GET /api/metadata/stats record_counts.bom. Covers temperature, rainfall, wind, and solar irradiance observations.',
    significance: 'Weather data enables energy-weather correlation. Gaps in BOM data reduce the accuracy of demand forecasting models.',
  },
  'analytics.ds.aer': {
    what: 'Total indexed records from the Australian Energy Regulator (AER) regulatory feed.',
    how: 'Count from GET /api/metadata/stats record_counts.aer. Includes pricing determinations, compliance documents, and rule changes.',
    significance: 'Regulatory data provides context for price movements. Low counts may indicate the AER scraper needs attention.',
  },
  'analytics.ds.aemo-duid': {
    what: 'Total indexed records for individual Dispatchable Unit Identifiers (DUIDs) from AEMO.',
    how: 'Count from GET /api/metadata/stats record_counts["aemo-duid"]. Covers per-generator bid stacks, capacity, rebid reasons, and dispatch intervals.',
    significance: 'DUID-level data enables generator-specific analysis — rebid patterns, capacity utilisation, and bidding behaviour across the NEM fleet.',
  },
  'analytics.ds.aemo-fcas': {
    what: 'Total indexed records from AEMO Frequency Control Ancillary Services (FCAS) markets.',
    how: 'Count from GET /api/metadata/stats record_counts["aemo-fcas"]. Covers all 8 FCAS markets: raise/lower for 6s, 60s, 5min, and regulation.',
    significance: 'FCAS data reveals grid stability dynamics. Cross-referencing with energy prices uncovers co-optimisation opportunities and frequency event correlations.',
  },
  'analytics.ds.regulatory': {
    what: 'Total indexed regulatory documents from Australian energy regulation frameworks.',
    how: 'Count from GET /api/metadata/stats record_counts.regulatory. Includes NER clauses, AER guidelines, AEMC rule changes, and AEMO procedures.',
    significance: 'Regulatory documents form the compliance backbone. Embedding-based search enables semantic matching of obligations to operational data.',
  },
  'analytics.ds.scholarly': {
    what: 'Total indexed scholarly research papers and publications.',
    how: 'Count from GET /api/metadata/stats record_counts.scholarly. Covers energy systems research, grid modernisation, renewables integration, and market design papers.',
    significance: 'Scholarly data enriches cross-domain analysis. Embedding correlations between research findings and operational data surface evidence-based insights.',
  },
  'analytics.ds.partner': {
    what: 'Total partner organisations discovered across quantum, photonics, and cross-sector ecosystems.',
    how: 'Count from GET /api/partners/stats total_organisations. Aggregated from multiple discovery sources including Quantum Navigator, Nature Index, and university databases.',
    significance: 'Partner data enables ecosystem mapping and collaboration discovery. Cross-domain embeddings reveal organisations with overlapping capabilities across sectors.',
  },

  'analytics.ds.energy-organisations': {
    what: 'Total indexed records for energy sector organisations across the NEM.',
    how: 'Count from GET /api/metadata/stats record_counts["energy-organisations"]. Covers generators, retailers, TNSPs, DNSPs, and market bodies.',
    significance: 'Enables organisational mapping and relationship analysis across the energy supply chain. Supports stakeholder identification and partnership discovery.',
  },
  'analytics.ds.grid-connection-queue': {
    what: 'Total indexed grid connection application records from AEMO.',
    how: 'Count from GET /api/metadata/stats record_counts["grid-connection-queue"]. Tracks connection enquiries, applications, and committed projects.',
    significance: 'Reveals the pipeline of new generation and storage projects seeking grid access. Essential for forecasting future supply mix and network augmentation needs.',
  },
  'analytics.ds.rez-capacity': {
    what: 'Total indexed Renewable Energy Zone capacity and allocation records.',
    how: 'Count from GET /api/metadata/stats record_counts["rez-capacity"]. Covers declared REZ areas, hosting capacity, and allocated MW.',
    significance: 'Tracks progress of REZ development across the NEM. Critical for assessing renewable energy build-out against ISP targets.',
  },
  'analytics.ds.ppa-intelligence': {
    what: 'Total indexed Power Purchase Agreement intelligence records.',
    how: 'Count from GET /api/metadata/stats record_counts["ppa-intelligence"]. Covers announced PPAs, pricing benchmarks, and counterparty data.',
    significance: 'Provides market intelligence on PPA pricing trends and deal structures. Essential for energy procurement strategy and contract benchmarking.',
  },
  'analytics.ds.network-constraints': {
    what: 'Total indexed network constraint records from AEMO dispatch data.',
    how: 'Count from GET /api/metadata/stats record_counts["network-constraints"]. Covers binding and violating constraints across interconnectors and intra-regional lines.',
    significance: 'Network constraints directly impact dispatch and pricing. Identifying constraint patterns enables better bidding strategy and investment decisions.',
  },
  'analytics.ds.planning-approvals': {
    what: 'Total indexed planning and environmental approval records for energy projects.',
    how: 'Count from GET /api/metadata/stats record_counts["planning-approvals"]. Covers state and federal approval stages, conditions, and timelines.',
    significance: 'Tracks regulatory approval progress for energy infrastructure. Delays in approvals are the leading cause of project timeline slippage.',
  },
  'analytics.ds.construction-milestones': {
    what: 'Total indexed construction milestone records for energy projects.',
    how: 'Count from GET /api/metadata/stats record_counts["construction-milestones"]. Covers financial close, construction start, commissioning, and commercial operation dates.',
    significance: 'Monitors physical progress of energy projects from approval through to operation. Enables tracking of ISP project delivery timelines.',
  },
  'analytics.ds.community-sentiment': {
    what: 'Total indexed community engagement and sentiment records related to energy projects.',
    how: 'Count from GET /api/metadata/stats record_counts["community-sentiment"]. Covers community feedback, social licence indicators, and stakeholder engagement outcomes.',
    significance: 'Community sentiment is a key risk factor for project delivery. Early detection of opposition enables proactive engagement strategies.',
  },

  // ─── Analytics Page: Charts ────────────────────────────────────────────
  'analytics.chart.insights-by-type': {
    what: 'Breakdown of processed records by insight category.',
    how: 'Derived from the by_type field in the analytics summary. Categories: embeddings_created, records_indexed, records_processed — proportionally split from pipeline totals.',
    significance: 'Shows the distribution of pipeline output. Imbalanced ratios between embeddings created and records indexed may indicate processing bottlenecks.',
  },
  'analytics.chart.type-distribution': {
    what: 'Pie chart showing the proportional split of insight types.',
    how: 'Same data as Insights by Type, rendered as percentage slices. Each slice = (type count / total insights) x 100.',
    significance: 'Quickly identifies dominant insight types. An even distribution suggests balanced analytics; heavy skew may indicate over-triggering of one algorithm.',
  },
  'analytics.chart.insights-trend': {
    what: 'Time-series of total insights generated per day over the selected period.',
    how: 'From the trends.trends array in GET /api/analytics. Each data point is the sum of insights for that day, plotted at daily granularity.',
    significance: 'Reveals ingestion cadence and seasonal patterns. Gaps indicate days with no pipeline runs; spikes may correlate with market events.',
  },

  // ─── Analytics Page: Performance Metrics ───────────────────────────────
  'analytics.perf.avg-processing': {
    what: 'Average time to process a single insight through the analytics pipeline.',
    how: 'From performance.processing_time.avg_ms in the analytics response. Measured from embedding lookup to insight persistence in DynamoDB.',
    significance: 'Target is under 200ms. Rising latency suggests Lambda cold starts, DynamoDB throttling, or increased embedding dimensionality.',
  },
  'analytics.perf.insights-processed': {
    what: 'Total number of insights that completed the full processing pipeline.',
    how: 'From performance.insights_processed. Counts only insights that passed validation, embedding lookup, and persistence stages.',
    significance: 'Should closely track total insights. A large gap between total and processed indicates pipeline failures or validation rejections.',
  },
  'analytics.perf.confidence-range': {
    what: 'The minimum and maximum confidence scores observed across all insights.',
    how: 'From performance.confidence.min and performance.confidence.max. Shows the full spread of model certainty.',
    significance: 'A narrow range (e.g., 80-95%) indicates consistent model behaviour. A wide range (e.g., 30-99%) suggests mixed data quality.',
  },
  'analytics.perf.algorithms': {
    what: 'Number of distinct algorithms used to generate insights.',
    how: 'Count of keys in performance.algorithms_used. Each key is an algorithm name (e.g., isolation_forest, dbscan, pearson_correlation).',
    significance: 'More algorithms means richer analysis. If only one algorithm is active, check that all Lambda functions are deployed and triggered.',
  },

  // ─── Insights Page ─────────────────────────────────────────────────────
  'insights.chart.type-distribution': {
    what: 'Pie chart of insight types across the current result set.',
    how: 'Client-side aggregation of the insight_type field from GET /api/insights. Grouped and counted per type.',
    significance: 'Helps identify which analytics algorithms are producing the most output. Useful for prioritising review of specific insight categories.',
  },
  'insights.chart.timeline': {
    what: 'Area chart showing insights created over time.',
    how: 'Client-side grouping of created_at dates from the insights list. Each point represents the count of insights created on that day.',
    significance: 'Reveals bursts of insight generation which often correlate with market events, weather extremes, or new data ingestion batches.',
  },
  'insights.row.type': {
    what: 'The category of analysis that produced this insight.',
    how: 'The insight_type field from the insights API. Set by the analytics Lambda based on which algorithm generated the insight.',
    significance: 'Different types require different review approaches: anomaly_detection insights need immediate attention, while aggregation insights are informational.',
  },
  'insights.row.confidence': {
    what: 'Model confidence score for this insight (0-100%).',
    how: 'From the provenance.confidence field in the insights API response. Represents the ML model\'s certainty when generating this insight.',
    significance: 'Critical: >=80% anomaly detections warrant immediate attention. High: >=85% or value >$150/MWh. Medium: >=75%. Low: <75% — consider manual validation.',
  },
  'insights.row.significance': {
    what: 'Severity classification assigned to this insight.',
    how: 'Computed server-side based on confidence score, value magnitude, and insight type. Critical > High > Medium > Low.',
    significance: 'Drives alert routing and review priority. Critical insights trigger webhook notifications; Low insights are for background monitoring.',
  },
  'insights.evidence-bundle': {
    what: 'A curated collection of related insights packaged for review or export.',
    how: 'Created via POST /api/evidence-bundles with selected insight IDs. The server aggregates the insights and their provenance chains.',
    significance: 'Bundles provide auditable evidence packages for compliance reporting and regulatory submissions to AEMO/AER.',
  },

  // ─── Insights Modal ─────────────────────────────────────────────────────
  'insights.modal.id': {
    what: 'Unique identifier for this insight record.',
    how: 'Generated as "insight-{N}" where N is the sequential index from the export/cross-domain API response.',
    significance: 'Use this ID to reference a specific insight in evidence bundles, exports, or when investigating provenance chains.',
  },
  'insights.modal.type': {
    what: 'The classification of analysis that produced this insight.',
    how: 'Derived from record content using keyword matching and value thresholds. Types: anomaly_detection, correlation, trend_analysis, pattern_recognition, similarity_score, cluster_analysis, aggregation.',
    significance: 'Determines how the insight should be interpreted: anomalies signal unusual activity, correlations reveal cross-source relationships, trends show directional patterns.',
  },
  'insights.modal.algorithm': {
    what: 'The ML algorithm version used to generate this insight.',
    how: 'From the metadata.algorithm field attached during embedding processing. Format: vX.Y where X is the major model version.',
    significance: 'Helps track which model version produced which insights. Useful for auditing and comparing accuracy across model upgrades.',
  },
  'insights.modal.date': {
    what: 'When this insight was created by the analytics pipeline.',
    how: 'The timestamp field from the OpenSearch record, set when the cross-domain query Lambda processes the embedding.',
    significance: 'Indicates data freshness. Insights from recent pipeline runs reflect current market conditions; older insights may be based on stale data.',
  },
  'insights.modal.source': {
    what: 'The source embedding that this insight was derived from.',
    how: 'Format: emb-{id}-{region}. Links this insight back to the original vector embedding stored in OpenSearch.',
    significance: 'Use this to trace the full provenance chain from raw data ingestion through to this insight via the Provenance page.',
  },
  'insights.modal.confidence': {
    what: 'How confident the ML model is in this insight (0-100%).',
    how: 'The cosine similarity or model confidence score from the embedding pipeline. Higher values indicate stronger signal.',
    significance: 'Above 85% indicates high reliability. Between 70-85% warrants review. Below 70% should be treated as exploratory and validated manually.',
  },

  // ─── Search Page ───────────────────────────────────────────────────────
  'search.result.score': {
    what: 'Semantic similarity score between your query and this result.',
    how: 'Calculated client-side: 60% weight on the insight\'s confidence score + 40% weight on keyword match ratio between query terms and the result\'s type/region/source text.',
    significance: 'Scores above 0.9 indicate near-exact semantic match. 0.7-0.9 is strong relevance. Below 0.5 is weak — the result may be tangentially related.',
  },
  'search.chart.facets': {
    what: 'Bar chart showing result distribution across NEM regions.',
    how: 'From GET /api/search/faceted with field=region. Counts how many indexed records exist per region (NSW1, VIC1, QLD1, SA1, TAS1).',
    significance: 'Uneven distribution may indicate regional data gaps. Ideally all active NEM regions should have comparable representation.',
  },
  'search.anomaly.score': {
    what: 'Anomaly detection score indicating how unusual this data point is.',
    how: 'From GET /api/search/anomalies. Scores are computed using isolation forest on the embedding space. Higher = more anomalous.',
    significance: 'Scores >=0.9 are strong anomalies warranting investigation. 0.7-0.9 are moderate. Below 0.5 is within normal variation.',
  },

  // ─── Provenance Page ───────────────────────────────────────────────────
  'provenance.chain': {
    what: 'The complete data lineage from raw ingestion to final insight.',
    how: 'From GET /api/provenance/{embedding_id}. Returns an ordered chain of stages: Ingested > Transformed > Embedded > Indexed > Analysed.',
    significance: 'Provides full auditability for any insight. Critical for regulatory compliance — every insight can be traced back to its source data.',
  },
  'provenance.aggregate': {
    what: 'Cross-datasource statistics aggregated by source, type, or confidence.',
    how: 'From POST /api/aggregate with the selected aggregation type. Runs a scan across the provenance table and groups results.',
    significance: 'Reveals system-wide patterns: which sources produce the most insights, which types dominate, and how confidence varies across the pipeline.',
  },

  // ─── Reports (Tier2Query) Page ─────────────────────────────────────────
  'reports.chart.correlation': {
    what: 'Dual-axis chart plotting energy price against temperature over time.',
    how: 'Demo data generated client-side with sinusoidal base patterns plus random noise. In production, uses AEMO price data joined with BOM temperature observations.',
    significance: 'Visualises the energy-weather relationship. Strong visual correlation supports the Pearson r value and informs demand forecasting.',
  },
  'reports.stat.pearson-r': {
    what: 'Pearson correlation coefficient between energy prices and temperature.',
    how: 'Calculated as the covariance of price and temperature divided by the product of their standard deviations. Range: -1 (inverse) to +1 (perfect positive).',
    significance: 'r > 0.7 indicates strong positive correlation (hotter = more expensive). r < 0.3 is weak. Used to validate weather-driven demand models.',
  },
  'reports.stat.total-insights': {
    what: 'Total insights included in this generated report.',
    how: 'Count from the analytics summary for the selected datasources and time range. Displayed in the report stats header.',
    significance: 'Contextualises the report scope. Low counts may indicate the date range is too narrow or the selected datasources have sparse data.',
  },
  'reports.stat.embeddings': {
    what: 'Number of unique embeddings referenced in the report.',
    how: 'From unique_source_embeddings in the analytics summary for the report\'s datasource and date range configuration.',
    significance: 'Higher values indicate the report draws from diverse data points. A report based on few embeddings may have limited analytical depth.',
  },
  'reports.section.trend': {
    what: 'Line chart of insights generated over the report period.',
    how: 'From trends.trends in the analytics API, filtered to the report\'s date range and datasources. Each point is one day\'s total.',
    significance: 'Shows whether insight generation is consistent or bursty. Consistent trends indicate reliable pipeline operation.',
  },

  // ─── Tier 3 Streaming Page ─────────────────────────────────────────────
  'streaming.stat.live-events': {
    what: 'Running count of events received since connecting to the stream.',
    how: 'Incremented client-side each time a WebSocket message or demo event is processed. Resets on disconnect.',
    significance: 'Indicates stream health. A stalled counter means the connection may be dead. Expected rate is 20-40 events per minute in production.',
  },
  'streaming.stat.queries': {
    what: 'Number of search queries processed through the live stream.',
    how: 'Subset of live events where type === "query". Includes both user-initiated and auto-triggered searches.',
    significance: 'High query volume indicates active usage. Queries also appear in the event stream for real-time monitoring.',
  },
  'streaming.stat.avg-latency': {
    what: 'Average response time across all events that reported latency.',
    how: 'Running average: (previous_avg * previous_count + new_latency) / new_count. Only events with a latency field contribute.',
    significance: 'Target is under 100ms for search queries. Rising latency indicates backend pressure or network issues.',
  },
  'streaming.stat.sources': {
    what: 'Number of active data sources feeding the real-time stream.',
    how: 'Currently static at 3 (AEMO, BOM, AER). In production, derived from distinct source values in the last 5 minutes of events.',
    significance: 'All 3 sources should be active. A missing source indicates its ingestion pipeline or WebSocket relay has failed.',
  },
  'streaming.chart.events-per-min': {
    what: 'Line chart of event throughput over the last 15-20 minutes.',
    how: 'Client-side bucketing of events by minute. Each point is the count of events received in that calendar minute.',
    significance: 'Visualises stream throughput trends. Sudden drops indicate connection issues; spikes correlate with market events or batch ingestion.',
  },
  'streaming.chart.type-distribution': {
    what: 'Pie chart of event types received in the current session.',
    how: 'Client-side count of events grouped by type (query, result, insight, alert, system).',
    significance: 'A healthy stream has a mix of types. If only system events appear, the analytics pipeline may not be generating insights.',
  },

  // ─── Export Page ───────────────────────────────────────────────────────
  'export.preview.id': {
    what: 'Unique identifier for each insight record.',
    how: 'The insight_id field from GET /api/insights. Format is a UUID generated at insight creation time.',
    significance: 'Used to reference specific insights in evidence bundles, provenance lookups, and support tickets.',
  },
  'export.preview.type': {
    what: 'The analytics algorithm that generated this insight.',
    how: 'The insight_type field. Set by the Lambda function that ran the analysis (e.g., anomaly_detection, pattern_recognition).',
    significance: 'Determines how the insight should be interpreted and which downstream actions it may trigger.',
  },
  'export.preview.confidence': {
    what: 'Model confidence for this specific insight (0-100%).',
    how: 'From provenance.confidence on each insight record. The ML model\'s self-assessed certainty for this particular output.',
    significance: 'Filter exports by confidence to focus on high-quality insights. Exporting low-confidence records is useful for model tuning.',
  },
  'export.format': {
    what: 'The file format for the exported data.',
    how: 'POST /api/export accepts format: "json" or "csv". JSON preserves nested structures; CSV flattens to tabular format.',
    significance: 'Use JSON for programmatic consumption and re-import. Use CSV for spreadsheet analysis, pivot tables, and sharing with non-technical stakeholders.',
  },

  // ─── Report Type Cards ────────────────────────────────────────────────
  'reports.type.energy-market-summary': {
    what: 'Comprehensive summary of National Electricity Market activity including spot prices, demand curves, and regional generation mix.',
    how: 'Aggregates AEMO dispatch data across selected regions and time range. Charts show price trends, demand profiles, and generation by fuel type.',
    significance: 'The foundational market report. Use for daily briefings, stakeholder updates, and tracking price/demand trends across NEM regions.',
  },
  'reports.type.weather-energy-correlation': {
    what: 'Statistical analysis of the relationship between weather conditions and energy market outcomes.',
    how: 'Joins BOM weather observations (temperature, solar irradiance, wind) with AEMO price/demand data. Calculates Pearson correlation coefficients per region.',
    significance: 'Quantifies weather-driven demand. Strong correlations (r > 0.7) validate forecasting models; weak correlations may indicate structural market changes.',
  },
  'reports.type.regulatory-impact': {
    what: 'Analysis of how AER regulatory decisions affect energy market pricing and participant behaviour.',
    how: 'Overlays AER determination timelines onto AEMO price trends. Identifies pre/post price shifts around regulatory events.',
    significance: 'Essential for compliance teams and regulatory submissions. Shows causal links between rule changes and market outcomes with supporting data.',
  },
  'reports.type.cross-source-intelligence': {
    what: 'Multi-datasource intelligence report leveraging semantic embeddings to discover cross-domain relationships.',
    how: 'Queries all datasources (AEMO, BOM, AER, Quantum) simultaneously using shared embedding space. Surfaces similarity clusters and unexpected correlations.',
    significance: 'The flagship cross-domain report. Demonstrates the unique value of the embedding platform by revealing insights invisible to single-source analysis.',
  },
  'reports.type.quantum-energy-nexus': {
    what: 'Cross-domain analysis mapping quantum technology capabilities to energy sector applications and opportunities.',
    how: 'Uses semantic embeddings to match quantum ecosystem entities (labs, technologies, facilities) with energy market data, identifying synergies.',
    significance: 'Strategic report for innovation teams. Identifies where quantum computing, sensing, or communication could address energy sector challenges.',
  },
  'reports.type.regulatory-submission': {
    what: 'AER-aligned regulatory submission with structured sections for Capital Expenditure, Operating Expenditure, RAB, and Service Classification.',
    how: 'Generates a submission-ready document using AER template structure. Populates with actual AEMO data and AER regulatory reference points.',
    significance: 'Reduces regulatory submission preparation effort. Pre-populates required sections with real data, maintaining AER format compliance.',
  },
  'reports.type.hedging-analysis': {
    what: 'Contract-vs-spot analysis evaluating hedging strategy performance with overlay charts and risk metrics.',
    how: 'Compares configured contract price against historical spot prices. Calculates hedge ratio, P&L, and value-at-risk across the analysis period.',
    significance: 'Critical for energy trading desks. Quantifies hedging effectiveness, identifies optimal contract structures, and models risk exposure under price scenarios.',
  },
  'reports.type.tnsp-benchmarking': {
    what: 'Performance comparison across Transmission Network Service Providers using reliability, cost, and augmentation metrics.',
    how: 'Pulls TNSP performance data from AEMO and AER sources. Generates radar charts and ranked tables across reliability, opex/km, and constraint hours.',
    significance: 'Used by regulators and TNSPs for benchmarking exercises. Identifies performance outliers and areas for improvement across the transmission network.',
  },
  'reports.type.fcas-market-analysis': {
    what: 'Detailed analysis of all 8 FCAS markets with DUID-level participation, pricing, and enablement patterns.',
    how: 'Aggregates FCAS market data across raise/lower services for 6s, 60s, 5min, and regulation. Shows per-DUID participation and market clearing prices.',
    significance: 'Essential for generators participating in FCAS. Reveals market depth, price patterns, and co-optimisation opportunities between energy and FCAS.',
  },
  'reports.type.embedding-similarity-map': {
    what: 'Visual map of semantic similarity clusters across all embedded datasources, revealing hidden cross-domain relationships.',
    how: 'Runs cosine similarity analysis across the full embedding space. Groups records into clusters where similarity exceeds configurable thresholds.',
    significance: 'The most powerful demonstration of embedding value. Shows how seemingly unrelated data from different domains connect through shared semantic meaning.',
  },
  'reports.type.cross-domain-anomaly': {
    what: 'Multi-source anomaly detection report identifying unusual patterns that span multiple datasources simultaneously.',
    how: 'Applies isolation forest across the combined embedding space. Flags records where anomaly scores exceed thresholds in 2+ datasources concurrently.',
    significance: 'Catches anomalies invisible to single-source analysis. A weather anomaly coinciding with a price spike and a regulatory filing signals systemic events.',
  },
  'reports.type.datasource-coverage': {
    what: 'Audit report showing data completeness, freshness, and coverage gaps across all ingested datasources.',
    how: 'Queries metadata stats for each datasource. Compares actual record counts, time spans, and last-updated timestamps against expected thresholds.',
    significance: 'Operational health check for the data platform. Identifies stale datasources, missing time periods, and ingestion pipeline failures before they affect analysis.',
  },

  // ─── Report Viewer (shared component) ──────────────────────────────────
  'report.stat.pipeline-runs': {
    what: 'Number of successful pipeline executions in the report period.',
    how: 'Count of ingestion history entries with status: SUCCEEDED from GET /api/ingestion?env=dev.',
    significance: 'Each run processes a batch of records through Fetch > Transform > Embed > Index. More runs = more up-to-date embeddings. The AEMO pipeline runs hourly via scheduled cron.',
  },
  'report.section.chart': {
    what: 'Visualisation of a specific data dimension within the report.',
    how: 'Data sourced from the report generation API or built client-side from analytics trends. Chart type (bar/line) chosen based on data characteristics.',
    significance: 'Charts surface patterns that are hard to spot in raw numbers. Look for trends, outliers, and comparisons across time periods.',
  },
  'report.section.table': {
    what: 'Tabular breakdown of a report dimension.',
    how: 'Rendered from the report sections array where type === "table". Data is typically a type or regional breakdown with counts and percentages.',
    significance: 'Provides precise values behind the charts. Useful for extracting specific numbers for presentations or compliance documentation.',
  },
};
